import api from '@/client/axiosInstance';
import { AdminRoute } from '@/constants';
import {
  BookingStatus,
  BrandName,
  BrandNameStatus,
  Consultant,
  ConsultantReferenceResponse,
  Franchise,
  PaymentsAdmin,
  ReferenceStatus,
  UserRole,
  SortOrder,
} from '@/types';
import axios from 'axios';

export const getDashboardData = async () => {
  try {
    const response = await api.get(AdminRoute.dashboard);
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching dashboard data');
    return [];
  }
};

export const getAdminPayments = async (filters: {
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
  meetingStartDate?: string;
  meetingEndDate?: string;
  paymentStartDate?: string;
  paymentEndDate?: string;
  refundStartDate?: string;
  refundEndDate?: string;
  completedStartDate?: string;
  completedEndDate?: string;
}): Promise<{
  total: number;
  page: number;
  limit: number;
  payments: PaymentsAdmin[];
}> => {
  try {
    const response = await api.get(AdminRoute.payments, {
      params: filters,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching payments');
    return {
      total: 0,
      page: 0,
      limit: 0,
      payments: [],
    };
  }
};

export const createFranchisor = async (data: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  franchise: string;
  role: UserRole;
}) => {
  try {
    const response = await api.post(AdminRoute.users, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Error creating user');
  }
};

// TCX-666: super-admin-only admin creation. The BE (PR #572) generates a
// one-time plaintext temporaryPassword for ADMIN / SUPER_ADMIN roles and
// returns it (served with Cache-Control: no-store). Unlike the swallow-and-log
// creators above, errors propagate here so the caller only surfaces the
// one-time password on a genuine success (and can show the 403 / duplicate
// message on failure).
export const createAdmin = async (data: {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole.ADMIN | UserRole.SUPER_ADMIN;
}): Promise<{ temporaryPassword?: string } & Record<string, unknown>> => {
  const response = await api.post(AdminRoute.users, data);
  return response.data;
};

export const createConsultant = async ({
  data,
  avatar,
}: {
  // TCX-507: serialized generically into FormData; accept either entity shape
  // (their `groups` types differ — Consultant: Group[], Franchise: string[]).
  data: Partial<Consultant> | Partial<Franchise>;
  avatar?: File;
}) => {
  try {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(`${key}[]`, item.toString());
          });
        } else {
          formData.append(key, value.toString());
        }
      }
    });

    if (avatar) {
      formData.append('logo', avatar);
    }

    const response = await api.post(AdminRoute.users, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    handleError(error, 'Error creating consultant');
  }
};

export const createFranchise = async ({
  data,
  avatar,
}: {
  data: Partial<Franchise>;
  avatar?: File;
}) => {
  try {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(`${key}[]`, item.toString());
          });
        } else {
          formData.append(key, value.toString());
        }
      }
    });

    if (avatar) {
      formData.append('logo', avatar);
    }

    const response = await api.post(AdminRoute.franchises, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    handleError(error, 'Error creating franchise');
  }
};

export const updateUser = async ({
  data,
  avatar,
  id,
}: {
  // TCX-507: accept either entity shape (groups types differ); serialized generically.
  data: Partial<Consultant> | Partial<Franchise>;
  avatar?: File;
  id: string;
}) => {
  try {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (value.length === 0) {
            // FormData has no native way to express an empty array.
            // Skipping the field entirely (the previous behaviour) made
            // the BE leave the existing DB value unchanged on
            // findByIdAndUpdate, so clearing e.g. Broker Group via the
            // admin consultant edit produced no DB write and the old
            // value reappeared on next fetch (TCX-372). Send one empty
            // entry so the key reaches the BE; the DTO transforms
            // [''] back to [] before persistence.
            formData.append(`${key}[]`, '');
          } else {
            value.forEach((item) => {
              formData.append(`${key}[]`, item.toString());
            });
          }
        } else {
          formData.append(key, value.toString());
        }
      }
    });

    if (avatar) {
      formData.append('profilePhoto', avatar);
    }

    const response = await api.patch(AdminRoute.updateUser(id), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    handleError(error, 'Error updating user');
  }
};

export const updateFranchise = async ({
  data,
  avatar,
  id,
}: {
  data: Partial<Franchise>;
  avatar?: File;
  id: string;
}) => {
  try {
    const formData = new FormData();
    const sanitizedKeys = ['established', 'startedFranchising'];

    Object.entries(data).forEach(([key, value]) => {
      if (
        sanitizedKeys.includes(key) ||
        (value !== undefined && value !== null)
      ) {
        if (Array.isArray(value)) {
          if (value.length === 0 && key === 'categories') {
            // A cleared multi-select sends []. An empty array appends nothing,
            // so the field would be absent and the backend would keep the old
            // value (cleared categories reappear — TCX-403). Send one empty
            // entry as a "clear" sentinel; the franchise DTO strips [''] -> [].
            formData.append(`${key}[]`, '');
          } else {
            value.forEach((item) => {
              formData.append(`${key}[]`, item.toString());
            });
          }
        } else if (sanitizedKeys.includes(key)) {
          // For date fields, allow empty string to unset
          formData.append(key, value ? value.toString() : '');
        } else if (typeof value === 'boolean') {
          // For booleans, always send 'true' or 'false' string
          formData.append(key, value.toString());
        } else {
          formData.append(key, value ? value.toString() : '');
        }
      }
    });

    if (avatar) {
      formData.append('logo', avatar);
    }

    const response = await api.patch(AdminRoute.updateFranchise(id), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    handleError(error, 'Error updating franchise');
  }
};

// TCX-843: admin-dismisses a pending Connect→premium upgrade request. Zero
// Stripe/billing side effects server-side; `dismissed: false` means the
// request had already resolved (Stripe plan change or another admin) before
// this click — the UI words its toast accordingly.
export const dismissUpgradeRequest = async (
  id: string,
): Promise<{ dismissed: boolean; franchise: Franchise }> => {
  try {
    const response = await api.post(AdminRoute.dismissUpgradeRequest(id));
    return response.data;
  } catch (error) {
    // handleError always throws; returning its `never` satisfies the declared
    // return type without a dead `throw` after it.
    return handleError(error, 'Error dismissing upgrade request');
  }
};

export const deleteFranchise = async (id: string) => {
  try {
    const response = await api.delete(AdminRoute.deleteFranchise(id));
    return response.data;
  } catch (error) {
    handleError(error, 'Error deleting franchise');
  }
};

export const deleteUser = async (id: string) => {
  try {
    const response = await api.delete(AdminRoute.deleteUser(id));
    return response.data;
  } catch (error) {
    handleError(error, 'Error deleting user');
  }
};

export const getDashboardGraph = async ({
  type,
  period,
}: {
  type: string;
  period: string;
}) => {
  try {
    const response = await api.get(AdminRoute.dashboardGraph, {
      params: {
        type,
        period,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching dashboard graph data');
    return [];
  }
};

const handleError = (error: unknown, message: string): never => {
  if (axios.isAxiosError(error)) {
    throw new Error(
      `${message}: ${error.response?.data?.message || error.message}`,
    );
  } else {
    throw new Error(`${message}: Unknown error occurred`);
  }
};

export const downloadPayments = async (filters: {
  startDate?: string;
  endDate?: string;
  search?: string;
  meetingStartDate?: string;
  meetingEndDate?: string;
  paymentStartDate?: string;
  paymentEndDate?: string;
  refundStartDate?: string;
  refundEndDate?: string;
  completedStartDate?: string;
  completedEndDate?: string;
}): Promise<any> => {
  try {
    const response = await api.get(AdminRoute.downloadPayment, {
      params: filters,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error downloading payments');
    return [];
  }
};

export const refundAdminPayment = async (
  sessionId: string,
): Promise<{
  id: string;
  isRefunded: boolean;
  refundedAt: string;
}> => {
  try {
    const response = await api.post(
      `${AdminRoute.payments}/${sessionId}/refund`,
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Error processing refund: ${
          error.response?.data?.message || error.message
        }`,
      );
    }
    throw new Error('Error processing refund: Unknown error occurred');
  }
};

export const downloadBookings = async (filters: {
  startDate?: string;
  endDate?: string;
  search?: string;
  franchiseId?: string;
  consultantId?: string;
}): Promise<any> => {
  try {
    const response = await api.get(AdminRoute.downloadBookings, {
      params: {
        startDate: filters.startDate,
        endDate: filters.endDate,
        search: filters.search,
        franchiseId: filters.franchiseId,
        consultantId: filters.consultantId,
        // Browser timezone so the server formats the CSV's dates in the same
        // zone the table is rendered in (TCX-574).
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error downloading bookings');
    return [];
  }
};


export const downloadFranchises = async (filters: { search?: string }) => {
  try {
    const { search } = filters;

    const response = await api.get(AdminRoute.downloadFranchises, {
      params: {
        search: search,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error downloading franchises');
    return [];
  }
};

export const downloadBrandMappings = async (filters: { search?: string }) => {
  try {
    const { search } = filters;

    const response = await api.get(AdminRoute.downloadBrandMappings, {
      params: {
        search: search,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error downloading brand mappings');
    return [];
  }
};

export const downloadCompanyBrands = async (filters: { search?: string }) => {
  try {
    const { search } = filters;

    const response = await api.get(AdminRoute.downloadCompanyBrands, {
      params: {
        search: search,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error downloading company brands');
    return [];
  }
};

export const downloadConsultants = async (filters: { search?: string }) => {
  try {
    const { search } = filters;

    const response = await api.get(AdminRoute.downloadConsultants, {
      params: {
        search: search,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error downloading consultants');
    return [];
  }
};

export const downloadReferences = async (filters: {
  search?: string;
  status?: ReferenceStatus;
}) => {
  try {
    const { search, status } = filters;

    const response = await api.get(AdminRoute.downloadReferences, {
      params: {
        search: search,
        status: status,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error downloading references');
    return [];
  }
};

export const getAdminBrandNames = async ({
  status,
  search,
  page,
  limit,
}: {
  status?: BrandNameStatus;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{
  total: number;
  page: number;
  limit: number;
  brandNames: BrandName[];
}> => {
  try {
    const response = await api.get(AdminRoute.brandNames, {
      params: { status, search, page, limit },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching brand names');
    return {
      total: 0,
      page: 0,
      limit: 0,
      brandNames: [],
    };
  }
};

export const updateBrandName = async (id: string, status: BrandNameStatus) => {
  try {
    const response = await api.patch(AdminRoute.updateBrandNames(id), {
      status,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error updating brand name');
  }
};

export const getAllFranchisors = async () => {
  try {
    const response = await api.get(AdminRoute.franchisors);
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching franchisors');
    return [];
  }
};

export const addExistingFranchisor = async (
  franchisorId: string,
  franchiseId: string,
) => {
  try {
    const response = await api.post(
      AdminRoute.addMemberFranchisor({ franchiseId, franchisorId }),
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error adding existing franchisor');
    return null;
  }
};

export const updateFranchableName = async (
  franchiseId: string,
  franchableName: string,
  isDelete: boolean = false,
) => {
  try {
    if (isDelete === true) {
      franchableName = '';
    }
    const response = await api.patch(AdminRoute.updateFranchise(franchiseId), {
      franchableName,
      franchableNameUpdatedAt: new Date().toISOString(),
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error updating franchable name');
  }
};

export const getAllSessions = async (
  filters: {
    search?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    tab?: 'franchisors' | 'consultants';
  },
  pagination: { page?: number; limit?: number },
) => {
  try {
    const response = await api.get('admin/sessions', {
      params: {
        ...pagination,
        search: filters.search,
        status: filters.status,
        startDate: filters.startDate,
        endDate: filters.endDate,
        tab: filters.tab,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching sessions');
    return [];
  }
};

export const downloadSessions = async (filters: {
  search?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  tab?: 'franchisors' | 'consultants';
}) => {
  const response = await api.get(AdminRoute.downloadSessions, {
    params: filters,
  });
  return response.data as { file: string };
};

export const updateSession = async (
  id: string,
  data: { rating: number; comment?: string },
) => {
  try {
    const response = await api.patch(`admin/sessions/${id}`, { ...data });
    return response.data;
  } catch (error) {
    handleError(error, 'Error updating session');
  }
};
export const getAdminConsultantReferences = async (filters: {
  search?: string;
  page?: number;
  limit?: number;
  status?: BrandNameStatus;
}): Promise<ConsultantReferenceResponse> => {
  try {
    const response = await api.get('admin/consultant-references', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching payments');
    return {
      total: 0,
      totalPages: 0,
      page: 0,
      limit: 0,
      references: [],
    };
  }
};

export const updateReferenceStatus = async (
  id: string,
  data: { status: BrandNameStatus },
) => {
  try {
    const response = await api.put(`admin/consultant-references/${id}`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Reference Update');
  }
};

export type FranchiseConsultantReviewsLatestResponse = {
  totalReviews: number;
  nps: number;
  ratingBreakdown: {
    franchiseOpportunityStrength: number;
    repEffectiveness: number;
    recommendationLikelihood: number;
  };
  latestReview: null | {
    id: string;
    rating: number;
    ratings?: Array<{ questionId: string; rating: number }>;
    comment: string;
    createdAt: string;
    consultant?: {
      id: string;
      firstName?: string;
      lastName?: string;
      profileImage?: string;
      email?: string;
    };
  };
};

export const getLatestConsultantReviewSummaryForFranchise = async (
  franchiseId: string,
): Promise<FranchiseConsultantReviewsLatestResponse> => {
  try {
    const response = await api.get(
      AdminRoute.franchiseConsultantReviewsLatest(franchiseId),
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching latest consultant review summary');
    return {
      totalReviews: 0,
      nps: 0,
      ratingBreakdown: {
        franchiseOpportunityStrength: 0,
        repEffectiveness: 0,
        recommendationLikelihood: 0,
      },
      latestReview: null,
    };
  }
};

export type FranchiseConsultantReviewItem = {
  id: string;
  comment: string;
  createdAt: string;
  consultant?: {
    id: string;
    firstName?: string;
    lastName?: string;
    profileImage?: string;
    email?: string;
  };
};

export type FranchiseConsultantReviewsResponse = {
  total: number;
  page: number;
  limit: number;
  reviews: FranchiseConsultantReviewItem[];
};

export const getConsultantReviewsForFranchise = async (
  franchiseId: string,
  params: { page?: number; limit?: number; sort?: SortOrder } = {},
): Promise<FranchiseConsultantReviewsResponse> => {
  try {
    const response = await api.get(AdminRoute.franchiseConsultantReviews(franchiseId), {
      params,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching consultant reviews');
    return { total: 0, page: params.page || 1, limit: params.limit || 5, reviews: [] };
  }
};

export const downloadConsultantReviewsForFranchise = async (
  franchiseId: string,
  params: { sort?: SortOrder } = {},
): Promise<{ file: string }> => {
  try {
    const response = await api.get(
      AdminRoute.downloadFranchiseReviews(franchiseId),
      {
        params,
      },
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error downloading consultant reviews');
    throw error;
  }
};
